import './breadcrumb.scss';

import type { BreadcrumbList } from 'schema-dts';

import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { Link as LinkSchema } from '@sola/schema/lib/schema.ts';

import { JsonLd } from '../../atoms/json-ld/json-ld.tsx';

// @TODO: Next Nav vs a tags.

export type BreadcrumbProps = {
  breadcrumbs: LinkSchema[];
  /**
   * Canonical page URL. When provided, the emitted BreadcrumbList gets a stable
   * `@id` of `${pageUrl}#BreadcrumbList` so other graph nodes (e.g. the WebPage
   * node) can reference it.
   */
  pageUrl?: string;
};

const getBreadcrumb = (
  bc: LinkSchema,
  isHome?: boolean,
  isLastItem?: boolean,
) => {
  const icon = isHome ? 'home-03' : 'chevron-right';
  return (
    <li
      key={bc.link}
      className={cn(
        'Breadcrumb__list-item flex gap-snug-xl items-center label-moderate label-md',
        {
          'Breadcrumb__breadcrumb-item': !isLastItem,
          'Breadcrumb__last-item': isLastItem,
        },
      )}
      aria-current={isLastItem ? 'page' : undefined}
    >
      <ArkIcon
        iconName={icon}
        className={cn({
          'Breadcrumb__chevron-separator': !isHome,
          'Breadcrumb__home-icon': isHome,
        })}
      />
      {!isHome && (
        <ArkIcon iconName="chevron-left" className="Breadcrumb__chevron-left" />
      )}
      {isLastItem ? (
        bc.title
      ) : (
        <Link href={bc.link} className="Breadcrumb__link">
          {bc.title}
        </Link>
      )}
    </li>
  );
};

export const Breadcrumb: React.FC<BreadcrumbProps> = ({
  breadcrumbs,
  pageUrl,
}) => {
  if (!breadcrumbs || breadcrumbs?.length < 2) {
    return null;
  }
  return (
    <nav
      className="Breadcrumb container flex items-center py-comfy-sm"
      aria-label="Breadcrumb"
    >
      <JsonLd<BreadcrumbList>
        item={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          ...(pageUrl ? { '@id': `${pageUrl}#BreadcrumbList` } : {}),
          itemListElement: breadcrumbs.map((bc, index) => {
            const absoluteLink = bc.link.startsWith('/')
              ? new URL(bc.link, process.env.NEXT_PUBLIC_BASE_URL_DOTCOM)
              : bc.link;
            return {
              '@type': 'ListItem',
              position: index + 1,
              name: bc.title,
              item: absoluteLink.toString(),
            };
          }),
        }}
      />

      <ol className="Breadcrumb__container gap-snug-xl flex items-center">
        {!!breadcrumbs.length &&
          breadcrumbs.map((bc, index) => {
            return getBreadcrumb(
              bc,
              index === 0,
              index === breadcrumbs.length - 1,
            );
          })}
      </ol>
    </nav>
  );
};
